// src/services/tripRatingService.js
import { db } from './firebase';
import { 
  collection, 
  doc, 
  addDoc, 
  updateDoc, 
  deleteDoc, 
  getDocs, 
  getDoc,
  query, 
  where, 
  orderBy,
  serverTimestamp 
} from 'firebase/firestore';

// 新增或更新用戶對行程的評分
export const rateTripByUser = async (tripId, userId, rating, comment = '') => {
  try {
    // 檢查用戶是否已經評分過
    const ratingsRef = collection(db, 'trip_ratings');
    const q = query(ratingsRef, where('tripId', '==', tripId), where('userId', '==', userId));
    const existingRatingQuery = await getDocs(q);

    const ratingData = {
      tripId,
      userId,
      rating: Number(rating),
      comment: comment.trim(),
      updatedAt: serverTimestamp()
    };

    let ratingId;
    
    if (!existingRatingQuery.empty) {
      // 更新現有評分
      const existingDoc = existingRatingQuery.docs[0];
      ratingId = existingDoc.id;
      const docRef = doc(db, 'trip_ratings', ratingId);
      await updateDoc(docRef, ratingData);
    } else {
      // 新增評分
      ratingData.createdAt = serverTimestamp();
      const docRef = await addDoc(ratingsRef, ratingData);
      ratingId = docRef.id;
    }

    // 重新計算行程的平均評分
    await updateTripAverageRating(tripId);
    
    return ratingId;
  } catch (error) {
    console.error('Error rating trip:', error);
    throw error;
  }
};

// 獲取行程的所有評分
export const getTripRatings = async (tripId) => {
  try {
    const ratingsRef = collection(db, 'trip_ratings');
    const q = query(ratingsRef, where('tripId', '==', tripId));
    const querySnapshot = await getDocs(q);

    const ratings = [];
    querySnapshot.forEach((doc) => {
      ratings.push({ id: doc.id, ...doc.data() });
    });

    // 在客戶端排序
    ratings.sort((a, b) => {
      const dateA = new Date(a.createdAt?.seconds * 1000 || a.createdAt || 0);
      const dateB = new Date(b.createdAt?.seconds * 1000 || b.createdAt || 0);
      return dateB - dateA;
    });

    return ratings;
  } catch (error) {
    console.error('Error getting trip ratings:', error);
    throw error;
  }
};

// 獲取用戶對特定行程的評分
export const getUserTripRating = async (tripId, userId) => {
  try {
    const ratingsRef = collection(db, 'trip_ratings');
    const q = query(ratingsRef, where('tripId', '==', tripId), where('userId', '==', userId));
    const ratingQuery = await getDocs(q);

    if (!ratingQuery.empty) {
      const docSnap = ratingQuery.docs[0];
      return { id: docSnap.id, ...docSnap.data() };
    }
    
    return null;
  } catch (error) {
    console.error('Error getting user trip rating:', error);
    throw error;
  }
};

// 計算並更新行程的平均評分
export const updateTripAverageRating = async (tripId) => {
  try {
    const ratingsRef = collection(db, 'trip_ratings');
    const q = query(ratingsRef, where('tripId', '==', tripId));
    const ratingsQuery = await getDocs(q);

    if (ratingsQuery.empty) {
      // 沒有評分，重置為0
      const tripRef = doc(db, 'trips', tripId);
      await updateDoc(tripRef, {
        'tripRating.totalRating': 0,
        'tripRating.ratingCount': 0,
        'tripRating.averageRating': 0
      });
      return { averageRating: 0, ratingCount: 0 };
    }

    let totalRating = 0;
    let ratingCount = 0;

    ratingsQuery.forEach((doc) => {
      const rating = doc.data().rating;
      totalRating += rating;
      ratingCount++;
    });

    const averageRating = totalRating / ratingCount;

    // 更新行程文件中的評分統計
    const tripRef = doc(db, 'trips', tripId);
    await updateDoc(tripRef, {
      'tripRating.totalRating': totalRating,
      'tripRating.ratingCount': ratingCount,
      'tripRating.averageRating': Number(averageRating.toFixed(1))
    });

    return {
      averageRating: Number(averageRating.toFixed(1)),
      ratingCount,
      totalRating
    };
  } catch (error) {
    console.error('Error updating trip average rating:', error);
    throw error;
  }
};

// 刪除用戶的行程評分
export const deleteUserTripRating = async (tripId, userId) => {
  try {
    const ratingsRef = collection(db, 'trip_ratings');
    const q = query(ratingsRef, where('tripId', '==', tripId), where('userId', '==', userId));
    const ratingQuery = await getDocs(q);

    if (!ratingQuery.empty) {
      const docSnap = ratingQuery.docs[0];
      const docRef = doc(db, 'trip_ratings', docSnap.id);
      await deleteDoc(docRef);
      
      // 重新計算平均評分
      await updateTripAverageRating(tripId);
    }
  } catch (error) {
    console.error('Error deleting trip rating:', error);
    throw error;
  }
};

// === 留言板功能 ===

// 新增留言
export const addTripComment = async (tripId, userId, userName, userPhoto, comment) => {
  try {
    const commentsRef = collection(db, 'trip_comments');
    const commentData = {
      tripId,
      userId,
      userName,
      userPhoto,
      comment: comment.trim(),
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    };

    const docRef = await addDoc(commentsRef, commentData);
    return docRef.id;
  } catch (error) {
    console.error('Error adding comment:', error);
    throw error;
  }
};

// 獲取行程的所有留言
export const getTripComments = async (tripId) => {
  try {
    const commentsRef = collection(db, 'trip_comments');
    const q = query(commentsRef, where('tripId', '==', tripId));
    const commentsQuery = await getDocs(q);

    const comments = [];
    commentsQuery.forEach((doc) => {
      comments.push({ id: doc.id, ...doc.data() });
    });

    // 在客戶端排序（最新的在上面）
    comments.sort((a, b) => {
      const dateA = new Date(a.createdAt?.seconds * 1000 || a.createdAt || 0);
      const dateB = new Date(b.createdAt?.seconds * 1000 || b.createdAt || 0);
      return dateB - dateA;
    });

    return comments;
  } catch (error) {
    console.error('Error getting trip comments:', error);
    throw error;
  }
};

// 刪除留言（只有留言者本人可以刪除）
export const deleteTripComment = async (commentId, userId) => {
  try {
    const commentRef = doc(db, 'trip_comments', commentId);
    const commentDoc = await getDoc(commentRef);
    
    if (!commentDoc.exists()) {
      throw new Error('留言不存在');
    }

    const commentData = commentDoc.data();
    if (commentData.userId !== userId) {
      throw new Error('只能刪除自己的留言');
    }

    await deleteDoc(commentRef);
  } catch (error) {
    console.error('Error deleting comment:', error);
    throw error;
  }
};

// 更新留言
export const updateTripComment = async (commentId, userId, newComment) => {
  try {
    const commentRef = doc(db, 'trip_comments', commentId);
    const commentDoc = await getDoc(commentRef);
    
    if (!commentDoc.exists()) {
      throw new Error('留言不存在');
    }

    const commentData = commentDoc.data();
    if (commentData.userId !== userId) {
      throw new Error('只能修改自己的留言');
    }

    await updateDoc(commentRef, {
      comment: newComment.trim(),
      updatedAt: serverTimestamp()
    });
  } catch (error) {
    console.error('Error updating comment:', error);
    throw error;
  }
};